import React, { useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Checkbox, Col, Form, InputNumber, Row, Select, Space, Spin, Typography } from "antd";
import { DownloadOutlined, EditOutlined, PictureOutlined, SearchOutlined } from "@ant-design/icons";
import { FaSave } from "react-icons/fa";
import HighchartsReact from "highcharts-react-official";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Highcharts from "../../Graficos/gerador/highcharts";
import { construirOpcoes } from "../../Graficos/gerador/modelos";
import { ALTURA_SAIDA, LARGURA_SAIDA } from "../../Graficos/gerador/tema";
import { baixarJpeg, gerarJpeg, montarHtmlImagem, salvarNoBanco } from "../../Graficos/gerador/exportar";
import { CHAVE_SPEC_IMPORTADO } from "../../Graficos/GeradorGraficos";
import { montarRanking, textoPeriodo } from "./montarGraficos";

const { Text } = Typography;

/**
 * Ranking dos estados de uma série, no formato de imagem da casa.
 *
 * O gráfico é um spec "barra-ranking" do Gerador de Gráficos, desenhado com
 * o mesmo motor — então a imagem gerada, o envio ao banco de imagens e a
 * inserção na matéria são exatamente os do gerador. "Editar no Gerador"
 * leva o spec pronto para ajustes finos (cores, textos, posição da caixa).
 */
export default function PainelEstados({ relatorio, metrico, chamarApi, ehPopup }) {
  const navigate = useNavigate();
  const chartRef = useRef(null);

  const series = useMemo(() => (relatorio?.series || []).filter((s) => s.porEstado !== false), [relatorio]);
  const semEstado = (relatorio?.series || []).filter((s) => s.porEstado === false);

  const [serieId, setSerieId] = useState(null);
  const [periodo, setPeriodo] = useState(null);
  const [top, setTop] = useState(5);
  const [mostrarTotal, setMostrarTotal] = useState(true);
  const [resposta, setResposta] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState(null);

  const serie = series.find((s) => s.id === serieId) || null;
  const respostaAtual = resposta?.serie === serieId ? resposta : null;

  const spec = useMemo(
    () => (serie && respostaAtual ? montarRanking(serie, respostaAtual, { top, metrico, mostrarTotal }) : null),
    [serie, respostaAtual, top, metrico, mostrarTotal]
  );
  const opcoes = useMemo(() => {
    if (!spec) return null;
    try {
      return construirOpcoes(spec);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [spec]);

  const buscar = async (periodoEscolhido = null) => {
    if (!serieId) return;
    setBuscando(true);
    setErro(null);
    try {
      const corpo = { serie: serieId, ...(periodoEscolhido || {}) };
      const r = await chamarApi("/nass/estados", { method: "POST", body: JSON.stringify(corpo) });
      setResposta(r);
      setPeriodo(r.periodo ? `${r.periodo.ano}-${r.periodo.mes ?? 0}` : null);
    } catch (e) {
      setErro({ mensagem: e.message, semChave: e.code === "NASS_SEM_CHAVE" });
    } finally {
      setBuscando(false);
    }
  };

  const escolherPeriodo = (chave) => {
    const [ano, mes] = chave.split("-").map(Number);
    buscar({ ano, mes: mes || null });
  };

  const legenda = spec ? spec.titulo.map((s) => s.texto).join("").trim() : "";

  const gerar = async (acao) => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    setGerando(true);
    try {
      const blob = await gerarJpeg(chart);
      if (acao === "baixar") {
        baixarJpeg(blob, legenda || "ranking_estados");
        return;
      }
      const { url } = await salvarNoBanco(blob, { legenda, comentario: `USDA NASS ${serie.id} por estado` });
      if (acao === "inserir") {
        await navigator.clipboard.writeText(montarHtmlImagem(url, legenda));
        toast.success(ehPopup ? "Gráfico enviado para a matéria!" : "Código copiado — cole na matéria.");
      } else {
        toast.success("Gráfico salvo no banco de imagens (editoria Gráficos).");
      }
    } catch (e) {
      toast.error(e.message || "Não foi possível gerar a imagem.");
    } finally {
      setGerando(false);
    }
  };

  const editarNoGerador = () => {
    try {
      sessionStorage.setItem(CHAVE_SPEC_IMPORTADO, JSON.stringify(spec));
    } catch {}
    navigate(`/graficos/gerador${ehPopup ? "?popup=1" : ""}`);
  };

  return (
    <>
      {erro?.semChave && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="A chave da Quick Stats não está configurada"
          description="Cadastre um e-mail em quickstats.nass.usda.gov/api e coloque a chave em NASS_API_KEY no .env da API."
        />
      )}
      {erro && !erro.semChave && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={erro.mensagem} />}
      {respostaAtual?.fromCache && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="A Quick Stats está indisponível — exibindo os dados da última consulta salva."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={6} xl={5}>
          <Card title="Ranking por estado" size="small">
            <Form layout="vertical" size="small">
              <Form.Item label="Série">
                <Select
                  value={serieId}
                  onChange={(v) => {
                    setSerieId(v);
                    setPeriodo(null);
                  }}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Selecione a série"
                  options={series.map((s) => ({ value: s.id, label: `${s.mercado} - ${s.atributo}` }))}
                />
              </Form.Item>
              {semEstado.length > 0 && (
                <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: -8, marginBottom: 8 }}>
                  {semEstado.map((s) => s.mercado).join(", ")}: o USDA só publica o total dos EUA.
                </Text>
              )}

              {respostaAtual?.periodosDisponiveis?.length > 0 && (
                <Form.Item label="Período">
                  <Select
                    value={periodo}
                    onChange={escolherPeriodo}
                    showSearch
                    optionFilterProp="label"
                    options={respostaAtual.periodosDisponiveis.map((p) => ({
                      value: `${p.ano}-${p.mes ?? 0}`,
                      label: textoPeriodo(serie, p),
                    }))}
                  />
                </Form.Item>
              )}

              <Form.Item label="Estados no gráfico">
                <InputNumber min={3} max={15} value={top} onChange={(v) => setTop(v || 5)} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item>
                <Checkbox checked={mostrarTotal} onChange={(e) => setMostrarTotal(e.target.checked)}>
                  Caixa com o total dos estados
                </Checkbox>
              </Form.Item>

              <Button type="primary" block icon={<SearchOutlined />} loading={buscando} disabled={!serieId} onClick={() => buscar()}>
                Buscar estados
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={18} xl={19}>
          <Card size="small">
            {buscando ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: ALTURA_SAIDA }}>
                <Spin size="large" />
              </div>
            ) : opcoes ? (
              <>
                <div style={{ background: "#2b2b2b", padding: 10, borderRadius: 4, overflowX: "auto" }}>
                  <div style={{ width: LARGURA_SAIDA, height: ALTURA_SAIDA, margin: "0 auto" }}>
                    <HighchartsReact key={`${serieId}-${periodo}`} ref={chartRef} highcharts={Highcharts} options={opcoes} />
                  </div>
                </div>
                {respostaAtual && (
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                    {respostaAtual.estados.length} estados publicados pelo USDA neste período. O total da caixa é a
                    soma deles — não é necessariamente o total dos Estados Unidos.
                  </Text>
                )}
                {!mostrarTotal || spec.extras.caixas.length ? null : (
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                    Esta série é uma média ou taxa: somar os estados não daria um total válido, então a caixa fica de fora.
                  </Text>
                )}
                <Space wrap style={{ marginTop: 12 }}>
                  <Button type="primary" icon={<PictureOutlined />} loading={gerando} onClick={() => gerar("inserir")}>
                    {ehPopup ? "Inserir na matéria" : "Gerar e copiar"}
                  </Button>
                  <Button icon={<FaSave />} loading={gerando} onClick={() => gerar("salvar")}>
                    Salvar no banco de imagens
                  </Button>
                  <Button icon={<DownloadOutlined />} loading={gerando} onClick={() => gerar("baixar")}>
                    Baixar JPG
                  </Button>
                  <Button icon={<EditOutlined />} onClick={editarNoGerador}>
                    Editar no Gerador
                  </Button>
                </Space>
              </>
            ) : (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
                <Typography.Title level={4} type="secondary" style={{ textAlign: "center" }}>
                  {respostaAtual && !respostaAtual.estados.length
                    ? "Nenhum estado com dado neste período."
                    : 'Escolha a série e clique em "Buscar estados"'}
                </Typography.Title>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}
